import type { PayInResponse } from '@payments/aurum-contracts';

const NESTED_AURUM_ERROR_GROUPS = ['deposit', 'payment'] as const;
const UNKNOWN_AURUM_ERROR_KEY = 'aurumErrors.unknown';

/** KB/API flat keys that map to a different leaf name in Chroma `global_sheets.deposit.aurumErrors`. */
const CHROMA_LEAF_ALIASES: Record<string, string> = {
  'aurumErrors.binBlocked': 'aurumErrors.errorBinBlocking',
  'aurumErrors.EncryptionError': 'aurumErrors.encryptionFailed',
};

type AurumErrorTranslationValues = Record<string, string | number>;
type AurumErrorTranslator = ((key: string, values?: AurumErrorTranslationValues) => string) & {
  has: (key: string) => boolean;
};

/**
 * Maps an Aurum API error key (underscore form) to a next-intl key under `global_sheets.deposit`.
 * Nested groups `deposit_` and `payment_` become `aurumErrors.deposit.*` / `aurumErrors.payment.*`;
 * other underscores in the suffix are kept (e.g. `wallet_pendingTransactions`).
 */
function toAurumTranslationKey(errorKey?: string): string {
  if (!errorKey) {
    return UNKNOWN_AURUM_ERROR_KEY;
  }

  if (!errorKey.startsWith('aurumErrors_')) {
    return applyChromaLeafAlias(errorKey.replace(/_/g, '.'));
  }

  const suffix = errorKey.slice('aurumErrors_'.length);
  const nestedGroup = NESTED_AURUM_ERROR_GROUPS.find(group => suffix.startsWith(`${group}_`));

  if (!nestedGroup) {
    return applyChromaLeafAlias(`aurumErrors.${suffix}`);
  }

  const nestedPath = suffix.slice(nestedGroup.length + 1).replace(/_/g, '.');
  return applyChromaLeafAlias(`aurumErrors.${nestedGroup}.${nestedPath}`);
}

function applyChromaLeafAlias(intlKey: string): string {
  return CHROMA_LEAF_ALIASES[intlKey] ?? intlKey;
}

function resolveAurumTranslationMessage(mappedKey: string, t: AurumErrorTranslator, values?: AurumErrorTranslationValues): string {
  const translationKey = t.has(mappedKey) ? mappedKey : UNKNOWN_AURUM_ERROR_KEY;

  if (values && Object.keys(values).length > 0) {
    return t(translationKey, values);
  }

  return t(translationKey);
}

function translateAurumErrorKey(errorKey: string | undefined, t: AurumErrorTranslator, values?: AurumErrorTranslationValues): string {
  return resolveAurumTranslationMessage(toAurumTranslationKey(errorKey), t, values);
}

/**
 * Resolves the player-facing message for a failed pay-in create response.
 * Aurum returns either a ready-made message or a translation key such as
 * `aurumErrors_deposit_walletLimitInsufficient`.
 */
function getPayInErrorMessage(errors: PayInResponse['errors'] | undefined, t: AurumErrorTranslator): string {
  const directMessage = errors?.errorMessages?.[0];
  if (directMessage) {
    return directMessage;
  }

  const errorKeyEntry = errors?.errorKeys?.[0];
  const params = errorKeyEntry?.params as AurumErrorTranslationValues | undefined;

  return translateAurumErrorKey(errorKeyEntry?.translationKey, t, params);
}

export { getPayInErrorMessage, toAurumTranslationKey, translateAurumErrorKey, UNKNOWN_AURUM_ERROR_KEY, type AurumErrorTranslator };
